const responseCache = new Map();
const pendingRequests = new Map();
const groupVersions = new Map();
const MAX_CACHE_ENTRIES = 250;

export const API_CACHE_TTL = {
  live: 10 * 1000,
  private: 20 * 1000,
  search: 30 * 1000,
  public: 60 * 1000,
  reference: 5 * 60 * 1000,
};

const abortError = () => {
  const error = new Error('The request was aborted');
  error.name = 'AbortError';
  return error;
};

const withCallerAbort = (promise, signal) => {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(abortError());
  return new Promise((resolve, reject) => {
    const onAbort = () => reject(abortError());
    signal.addEventListener('abort', onAbort, { once: true });
    promise.then(
      (value) => { signal.removeEventListener('abort', onAbort); resolve(value); },
      (error) => { signal.removeEventListener('abort', onAbort); reject(error); }
    );
  });
};

const versionKey = (group, scope) => `${scope}|${group}`;
const currentVersion = (group, scope) => groupVersions.get(versionKey(group, scope)) || 0;

const prune = (now) => {
  responseCache.forEach((entry, key) => {
    if (entry.expiresAt <= now) responseCache.delete(key);
  });
  while (responseCache.size >= MAX_CACHE_ENTRIES) {
    responseCache.delete(responseCache.keys().next().value);
  }
};

export const apiCacheScope = (user, fallback = 'guest') => {
  const id = user?.id || user?.userid || user?.idusers || user?.email;
  return id ? `user:${id}` : fallback;
};

export const apiAuthHeaders = (user, extra = {}) => {
  const token = user ? localStorage.getItem('token') : null;
  return { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...extra };
};

export const cachedApiJson = (url, {
  group = 'default',
  scope = 'public',
  language = 'en',
  variant = '',
  ttlMs = API_CACHE_TTL.public,
  headers = {},
  signal,
} = {}) => {
  const version = currentVersion(group, scope);
  const key = `${group}|${scope}|${language}|${variant}|v${version}|${String(url)}`;
  const now = Date.now();
  const cached = responseCache.get(key);
  if (cached && cached.expiresAt > now) return withCallerAbort(Promise.resolve(cached.data), signal);
  if (cached) responseCache.delete(key);

  let request = pendingRequests.get(key);
  if (!request) {
    request = fetch(url, { headers })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          const error = new Error(data?.error || `Request failed with status ${response.status}`);
          error.status = response.status;
          error.data = data;
          throw error;
        }
        prune(Date.now());
        responseCache.set(key, {
          data,
          expiresAt: Date.now() + ttlMs,
          group,
          scope,
        });
        return data;
      })
      .finally(() => pendingRequests.delete(key));
    pendingRequests.set(key, request);
  }
  return withCallerAbort(request, signal);
};

export const invalidateApiCache = ({ groups, scope = 'public' }) => {
  const requested = Array.isArray(groups) ? groups : [groups];
  requested.filter(Boolean).forEach((group) => {
    const key = versionKey(group, scope);
    groupVersions.set(key, currentVersion(group, scope) + 1);
  });
  responseCache.forEach((entry, key) => {
    if (entry.scope === scope && requested.includes(entry.group)) responseCache.delete(key);
  });
};

export const invalidateOrderCaches = (user, orderId) => {
  const groups = ['orders'];
  if (orderId !== undefined && orderId !== null && orderId !== '') groups.push(`order:${orderId}`);
  invalidateApiCache({ groups, scope: apiCacheScope(user) });
};

export const invalidateProductCaches = (user) => {
  invalidateApiCache({
    groups: ['seller-products', 'products', 'search-results'],
    scope: apiCacheScope(user),
  });
  invalidateApiCache({
    groups: ['product-recommendations', 'search-filters'],
    scope: 'public',
  });
};

export const clearApiCache = () => {
  responseCache.clear();
  pendingRequests.clear();
  groupVersions.clear();
};
